import { lumi } from "../lib/lumi"
import { useState, useEffect } from "react"
import * as XLSX from "xlsx"
import { useAuth } from "../hooks/useAuth"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts"

type TimeEntry = {
  _id: string
  userId: string
  userName: string
  teamId: string
  teamName: string
  projectId: string
  projectName: string
  projectCategory: string
  weekStart: string
  weekEnd: string
  dailyEntries: DailyEntry[]
  totalHours: number
  status: string
  isTemporary: boolean
  createdAt: string
}

type DailyEntry = {
  date: string
  dayOfWeek: string
  isHoliday: boolean
  vacationType: string
  activities: Activity[]
}

type Activity = {
  activityCode: string
  hours: number
  tags: string[]
}

type Team = {
  _id: string
  teamId: string
  teamName: string
}

type UserProfile = {
  _id: string
  userId: string
  userName: string
  teamId: string
  teamName: string
}

type Project = {
  _id: string
  projectId: string
  projectName: string
  category: string
}

export function AdminDashboard() {
  const { isCLevel, userRole } = useAuth()
  const [activeTab, setActiveTab] = useState<"dashboard">("dashboard")
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [users, setUsers] = useState<UserProfile[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(false)
  
  // 필터
  const [selectedTeam, setSelectedTeam] = useState<string>("all")
  const [selectedUser, setSelectedUser] = useState<string>("all")
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  

  
  // 프로젝트별 그래프 선택 상태
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set())
  
  // 직원별 그래프 선택 상태
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadDashboardData()
  }, [])

  // 2단계 최적화: 기간 필터를 서버 조회로 이동 (startDate/endDate 변경 시 재조회)
  useEffect(() => {
    loadDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // 2단계 최적화: time_entries2 서버 사이드 필터
      // - draft(임시저장) 제외를 서버에서 처리
      // - 기간(weekStart) 범위를 서버에서 제한하여 전송량 감소
      const entryFilter: Record<string, any> = { status: { $ne: "draft" } }
      if (startDate || endDate) {
        const weekStartCond: Record<string, any> = {}
        if (startDate) {
          // 주 단위 겹침을 고려해 시작일에서 7일 여유를 둔다
          const s = new Date(startDate)
          s.setDate(s.getDate() - 7)
          weekStartCond.$gte = s.toISOString()
        }
        if (endDate) {
          const e = new Date(endDate)
          e.setHours(23, 59, 59, 999)
          weekStartCond.$lte = e.toISOString()
        }
        entryFilter.weekStart = weekStartCond
      }

      const [entriesResult, teamsResult, usersResult, projectsResult] = await Promise.all([
        lumi.entities.time_entries2.list({ filter: entryFilter, limit: 5000 }),
        lumi.entities.teams.list({ filter: { enabled: true } }),
        // 모든 역할(USER, MANAGER 등) 포함하여 활성화된 사용자 로드
        lumi.entities.userProfiles3.list({ limit: 1000 }),
        lumi.entities.projects.list({ limit: 1000 })
      ])
      setTimeEntries(entriesResult.list as TimeEntry[])
      // CEO 팀 제외한 팀 목록만 저장
      const filteredTeams = (teamsResult.list as Team[]).filter(t => t.teamName !== "CEO")
      setTeams(filteredTeams)
      // 활성화된 사용자만 필터링 (역할 무관: USER, MANAGER, C_LEVEL 등 모두 포함)
      const enabledUsers = (usersResult.list as UserProfile[]).filter(u => u.enabled !== false)
      setUsers(enabledUsers)
      setProjects(projectsResult.list as Project[])
    } catch (error) {
      console.error("Failed to load dashboard data:", error)
      alert("❌ 데이터 로딩에 실패했습니다.")
    } finally {
      setLoading(false)
    }
  }





  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ko-KR")
  }

  const handleExportToExcel = () => {
    // 활동코드 목록 (고정된 순서)
    const activityCodes = [
      "개발/설계",
      "영업/견적/PM/기획",
      "제작/조립/구매",
      "시험/검증/테스트",
      "설치/납품/AS",
      "지원/문서/교육",
      "미팅",
      "이동"
    ]

    // 직원별로 데이터 그룹화
    const userGrouped: Record<string, TimeEntry[]> = {}
    filteredEntries.forEach((entry) => {
      const key = entry.userId
      if (!userGrouped[key]) {
        userGrouped[key] = []
      }
      userGrouped[key].push(entry)
    })

    // Excel 워크북 생성
    const wb = XLSX.utils.book_new()

    // 각 직원별로 시트 생성
    Object.entries(userGrouped).forEach(([userId, entries]) => {
      const userName = entries[0]?.userName || "알 수 없음"
      const teamName = entries[0]?.teamName || "미지정"

      // 프로젝트별, 활동코드별 시간 집계
      const projectActivityMatrix: Record<string, Record<string, number>> = {}

      entries.forEach((entry) => {
        const projectKey = entry.projectName
        if (!projectActivityMatrix[projectKey]) {
          projectActivityMatrix[projectKey] = {}
          activityCodes.forEach(code => {
            projectActivityMatrix[projectKey][code] = 0
          })
        }

        entry.dailyEntries?.forEach((day) => {
          day.activities?.forEach((activity) => {
            if (activityCodes.includes(activity.activityCode)) {
              projectActivityMatrix[projectKey][activity.activityCode] += activity.hours || 0
            }
          })
        })
      })

      // 2차원 배열로 변환 (헤더 + 데이터)
      const sheetData: any[][] = []
      
      // 헤더 행: 첫 번째 열은 "프로젝트명", 나머지는 활동코드
      sheetData.push(["프로젝트명", ...activityCodes, "합계"])

      // 데이터 행: 각 프로젝트별로 활동코드별 시간
      Object.entries(projectActivityMatrix).forEach(([projectName, activities]) => {
        const row = [projectName]
        let rowTotal = 0
        activityCodes.forEach(code => {
          const hours = activities[code] || 0
          row.push(hours)
          rowTotal += hours
        })
        row.push(rowTotal)
        sheetData.push(row)
      })

      // 합계 행 추가
      const totalRow = ["합계"]
      let grandTotal = 0
      activityCodes.forEach((code, index) => {
        let colTotal = 0
        for (let i = 1; i < sheetData.length; i++) {
          colTotal += sheetData[i][index + 1] || 0
        }
        totalRow.push(colTotal)
        grandTotal += colTotal
      })
      totalRow.push(grandTotal)
      sheetData.push(totalRow)

      // 시트 생성
      const ws = XLSX.utils.aoa_to_sheet(sheetData)

      // 컬럼 너비 설정
      const wscols = [
        { wch: 30 }, // 프로젝트명
        ...activityCodes.map(() => ({ wch: 18 })), // 각 활동코드
        { wch: 12 } // 합계
      ]
      ws["!cols"] = wscols

      // 시트명: 직원명 (팀명)
      let sheetName = `${userName} (${teamName})`
      // Excel 시트명 길이 제한 (31자) 및 특수문자 제거
      sheetName = sheetName.replace(/[:\\/?*\[\]]/g, "").substring(0, 31)

      XLSX.utils.book_append_sheet(wb, ws, sheetName)
    })

    // 파일명 생성 (현재 필터 정보 포함)
    let fileName = "투입시간_데이터"
    if (selectedTeam !== "all") {
      const team = teams.find(t => t.teamId === selectedTeam)
      fileName += `_${team?.teamName || selectedTeam}`
    }
    if (selectedUser !== "all") {
      const user = users.find(u => u.userId === selectedUser)
      fileName += `_${user?.userName || selectedUser}`
    }
    fileName += `_${new Date().toISOString().split("T")[0]}.xlsx`

    // 파일 다운로드
    XLSX.writeFile(wb, fileName)
    alert("✅ Excel 파일이 다운로드되었습니다.")
  }

  // 필터링된 데이터 (대시보드용) - 연차 제외 + 임시저장 제외 + CEO 팀 제외
  const filteredEntries = timeEntries.filter((entry) => {
    // 연차 관련 프로젝트 제외
    if (entry.projectName === "연차" || entry.projectName === "오전반차" || entry.projectName === "오후반차") return false
    // 임시저장(draft) 상태 제외
    if (entry.status === "draft") return false
    // CEO 팀 데이터 제외
    if (entry.teamName === "CEO") return false
    
    // 팀 필터링 먼저 적용
    if (selectedTeam !== "all" && entry.teamId !== selectedTeam) return false
    
    // 사용자 필터링 적용
    if (selectedUser !== "all" && entry.userId !== selectedUser) return false
    
    // 기간 필터링 적용
    if (startDate || endDate) {
      const entryStart = new Date(entry.weekStart)
      const entryEnd = new Date(entry.weekEnd)
      
      if (startDate) {
        const filterStart = new Date(startDate)
        // 입력 기간의 종료일이 필터 시작일보다 이전이면 제외
        if (entryEnd < filterStart) return false
      }
      
      if (endDate) {
        const filterEnd = new Date(endDate)
        // 입력 기간의 시작일이 필터 종료일보다 이후이면 제외
        if (entryStart > filterEnd) return false
      }
    }
    
    // 비활성화된 사용자 데이터 제외 (users 배열이 로드되었을 때만)
    if (users.length > 0) {
      const userExists = users.find(u => u.userId === entry.userId)
      if (!userExists) return false
    }
    
    return true
  })



  // 통계 계산
  const stats = {
    totalProjects: new Set(filteredEntries.map(e => e.projectId)).size,
    totalHours: filteredEntries.reduce((sum, e) => sum + (e.totalHours || 0), 0),
    activeUsers: new Set(filteredEntries.map(e => e.userId)).size,
    thisWeekHours: filteredEntries
      .filter(e => {
        const weekStart = new Date(e.weekStart)
        const now = new Date()
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        return weekStart >= weekAgo
      })
      .reduce((sum, e) => sum + (e.totalHours || 0), 0)
  }

  // 프로젝트별 투입시간 집계 (활동코드별 포함)
  const projectStats = filteredEntries.reduce((acc, entry) => {
    const key = entry.projectId
    if (!acc[key]) {
      acc[key] = {
        projectName: entry.projectName,
        projectCategory: entry.projectCategory,
        totalHours: 0,
        activityBreakdown: {} as Record<string, number>
      }
    }
    acc[key].totalHours += entry.totalHours || 0
    
    // 활동코드별 시간 집계
    entry.dailyEntries?.forEach((day) => {
      day.activities?.forEach((activity) => {
        if (!acc[key].activityBreakdown[activity.activityCode]) {
          acc[key].activityBreakdown[activity.activityCode] = 0
        }
        acc[key].activityBreakdown[activity.activityCode] += activity.hours || 0
      })
    })
    
    return acc
  }, {} as Record<string, { projectName: string; projectCategory: string; totalHours: number; activityBreakdown: Record<string, number> }>)

  const projectList = Object.values(projectStats).sort((a, b) => b.totalHours - a.totalHours)

  // 초기 로드 시 상위 10개 프로젝트 선택
  useEffect(() => {
    if (projectList.length > 0 && selectedProjects.size === 0) {
      const top10 = new Set(projectList.slice(0, 10).map((_, idx) => idx.toString()))
      setSelectedProjects(top10)
    }
  }, [projectList.length])

  // 활동 코드별 투입시간 집계
  const activityStats = filteredEntries.reduce((acc, entry) => {
    entry.dailyEntries?.forEach((day) => {
      day.activities?.forEach((activity) => {
        if (!acc[activity.activityCode]) {
          acc[activity.activityCode] = 0
        }
        acc[activity.activityCode] += activity.hours || 0
      })
    })
    return acc
  }, {} as Record<string, number>)

  const activityList = Object.entries(activityStats)
    .map(([code, hours]) => ({ activityCode: code, hours }))
    .sort((a, b) => b.hours - a.hours)

  // 팀별 투입시간 집계
  const teamStats = filteredEntries.reduce((acc, entry) => {
    const key = entry.teamId || "미지정"
    if (!acc[key]) {
      acc[key] = {
        teamName: entry.teamName || "미지정",
        totalHours: 0,
        userCount: new Set()
      }
    }
    acc[key].totalHours += entry.totalHours || 0
    acc[key].userCount.add(entry.userId)
    return acc
  }, {} as Record<string, { teamName: string; totalHours: number; userCount: Set<string> }>)

  const teamList = Object.values(teamStats).map(team => ({
    ...team,
    userCount: team.userCount.size
  })).sort((a, b) => {
    // "임원" 팀을 맨 아래로
    if (a.teamName === "임원") return 1
    if (b.teamName === "임원") return -1
    // 나머지는 투입시간 순
    return b.totalHours - a.totalHours
  })

  // 카테고리별 색상
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "납품장비": return "bg-blue-100 text-blue-700"
      case "제품개발": return "bg-green-100 text-green-700"
      case "연구개발": return "bg-purple-100 text-purple-700"
      case "경영지원": return "bg-orange-100 text-orange-700"
      default: return "bg-gray-100 text-gray-700"
    }
  }

  // 활동 코드별 색상
  const getActivityColor = (code: string) => {
    const colors: Record<string, string> = {
      "개발/설계": "#3B82F6",
      "영업/견적/PM/기획": "#10B981",
      "제작/조립/구매": "#EAB308",
      "시험/검증/테스트": "#EF4444",
      "설치/납품/AS": "#A855F7",
      "지원/문서/교육": "#EC4899",
      "미팅": "#6366F1",
      "이동": "#14B8A6"
    }
    return colors[code] || "#6B7280"
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">관리자 대시보드</h2>
        <p className="text-gray-600">프로젝트별, 활동별 투입 시간을 실시간으로 확인하세요</p>
      </div>



      {/* Dashboard Tab */}
      {activeTab === "dashboard" && (
        <>
          {/* 필터 영역 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            {/* 첫 번째 줄: 팀, 직원, 버튼들 */}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 lg:gap-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-2 flex-1">
                <label className="text-sm font-medium text-gray-700 lg:whitespace-nowrap">팀:</label>
                <select
                  value={selectedTeam}
                  onChange={(e) => {
                    setSelectedTeam(e.target.value)
                    setSelectedUser("all")
                  }}
                  className="w-full lg:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  <option value="all">전체 팀</option>
                  {teams.map((team) => (
                    <option key={team._id} value={team.teamId}>
                      {team.teamName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col lg:flex-row lg:items-center gap-2 flex-1">
                <label className="text-sm font-medium text-gray-700 lg:whitespace-nowrap">직원:</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="w-full lg:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  <option value="all">전체 직원</option>
                  {users
                    .filter(u => selectedTeam === "all" || u.teamId === selectedTeam)
                    .map((user) => (
                      <option key={user._id} value={user.userId}>
                        {user.userName} ({user.teamName})
                      </option>
                    ))}
                </select>
              </div>

              <button
                onClick={loadDashboardData}
                disabled={loading}
                className="w-full lg:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                {loading ? "로딩 중..." : "🔄 새로고침"}
              </button>

              <button
                onClick={handleExportToExcel}
                disabled={loading || filteredEntries.length === 0}
                className="w-full lg:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              >
                📥 Excel 다운로드
              </button>
            </div>

            {/* 기간 선택 */}
            <div className="border-t border-gray-200 pt-4">
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">기간:</label>
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                  <span className="text-gray-500">~</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                </div>
                {(startDate || endDate) && (
                  <button
                    onClick={() => {
                      setStartDate("")
                      setEndDate("")
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    🔄 기간 초기화
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">총 프로젝트</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalProjects}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">총 투입 시간</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalHours}h</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">활동 인원</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeUsers}명</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">이번 주</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.thisWeekHours}h</p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* 프로젝트별 누적 막대 그래프 (Stacked Bar Chart) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">프로젝트별 투입 시간 (활동코드별 누적)</h3>
            {projectList.length === 0 ? (
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <p className="text-gray-500">데이터가 없습니다</p>
              </div>
            ) : (
              <>
                {/* 프로젝트 선택 체크박스 */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">표시할 프로젝트 선택:</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          // 전체선택
                          const allProjects = new Set(projectList.map((_, idx) => idx.toString()))
                          setSelectedProjects(allProjects)
                        }}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                      >
                        전체선택
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => {
                          // 전체해제
                          setSelectedProjects(new Set())
                        }}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                      >
                        전체해제
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => {
                          // 상위 10개로 초기화
                          const top10 = new Set(projectList.slice(0, 10).map((_, idx) => idx.toString()))
                          setSelectedProjects(top10)
                        }}
                        className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                      >
                        상위 10개 선택
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                    {projectList.map((project, idx) => (
                      <label key={idx} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedProjects.has(idx.toString())}
                          onChange={(e) => {
                            const newSelected = new Set(selectedProjects)
                            if (e.target.checked) {
                              newSelected.add(idx.toString())
                            } else {
                              newSelected.delete(idx.toString())
                            }
                            setSelectedProjects(newSelected)
                          }}
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-gray-700 truncate" title={project.projectName}>
                          {project.projectName}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 그래프 */}
                <div className="h-[576px] sm:h-[800px] md:h-[576px]">
                  <ResponsiveContainer width="100%" height="100%">
                    {/* 모바일: 가로 막대 그래프 (horizontal) */}
                    <div className="block md:hidden h-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={projectList
                            .filter((_, idx) => selectedProjects.has(idx.toString()))
                            .map(p => ({
                              name: p.projectName.length > 12 ? p.projectName.substring(0, 12) + '...' : p.projectName,
                              fullName: p.projectName,
                              ...p.activityBreakdown
                            }))}
                          layout="vertical"
                          margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" label={{ value: '시간 (h)', position: 'insideBottom', offset: -5 }} />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            width={0}
                            tick={false}
                          />
                          <Tooltip 
                            labelFormatter={(label, payload) => {
                              if (payload && payload[0]) {
                                return payload[0].payload.fullName
                              }
                              return label
                            }}
                          />
                          <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '10px' }} />
                          <Bar dataKey="개발/설계" stackId="a" fill="#3B82F6" name="개발/설계" />
                          <Bar dataKey="영업/견적/PM/기획" stackId="a" fill="#10B981" name="영업/견적/PM/기획" />
                          <Bar dataKey="제작/조립/구매" stackId="a" fill="#EAB308" name="제작/조립/구매" />
                          <Bar dataKey="시험/검증/테스트" stackId="a" fill="#EF4444" name="시험/검증/테스트" />
                          <Bar dataKey="설치/납품/AS" stackId="a" fill="#A855F7" name="설치/납품/AS" />
                          <Bar dataKey="지원/문서/교육" stackId="a" fill="#EC4899" name="지원/문서/교육" />
                          <Bar dataKey="미팅" stackId="a" fill="#6366F1" name="미팅" />
                          <Bar dataKey="이동" stackId="a" fill="#14B8A6" name="이동" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* 데스크톱: 세로 막대 그래프 (vertical) */}
                    <div className="hidden md:block h-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={projectList
                            .filter((_, idx) => selectedProjects.has(idx.toString()))
                            .map(p => ({
                              name: p.projectName.length > 15 ? p.projectName.substring(0, 15) + '...' : p.projectName,
                              fullName: p.projectName,
                              ...p.activityBreakdown
                            }))}
                          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} style={{ fontSize: '12px' }} />
                          <YAxis label={{ value: '시간 (h)', angle: -90, position: 'insideLeft' }} />
                          <Tooltip 
                            labelFormatter={(label, payload) => {
                              if (payload && payload[0]) {
                                return payload[0].payload.fullName
                              }
                              return label
                            }}
                          />
                          <Legend wrapperStyle={{ paddingTop: '20px' }} />
                          <Bar dataKey="개발/설계" stackId="a" fill="#3B82F6" name="개발/설계" />
                          <Bar dataKey="영업/견적/PM/기획" stackId="a" fill="#10B981" name="영업/견적/PM/기획" />
                          <Bar dataKey="제작/조립/구매" stackId="a" fill="#EAB308" name="제작/조립/구매" />
                          <Bar dataKey="시험/검증/테스트" stackId="a" fill="#EF4444" name="시험/검증/테스트" />
                          <Bar dataKey="설치/납품/AS" stackId="a" fill="#A855F7" name="설치/납품/AS" />
                          <Bar dataKey="지원/문서/교육" stackId="a" fill="#EC4899" name="지원/문서/교육" />
                          <Bar dataKey="미팅" stackId="a" fill="#6366F1" name="미팅" />
                          <Bar dataKey="이동" stackId="a" fill="#14B8A6" name="이동" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>

          {/* 개인별 활동 코드별 투입시간 (Grouped Bar Chart) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">개인별 활동 코드별 투입시간</h3>
            {(() => {
              // 개인별 활동코드별 집계
              const userActivityStats = filteredEntries.reduce((acc, entry) => {
                const key = entry.userId
                if (!acc[key]) {
                  acc[key] = {
                    userName: entry.userName,
                    teamName: entry.teamName,
                    userRole: users.find(u => u.userId === entry.userId)?.role || 'USER',
                    activities: {
                      "개발/설계": 0,
                      "영업/견적/PM/기획": 0,
                      "제작/조립/구매": 0,
                      "시험/검증/테스트": 0,
                      "설치/납품/AS": 0,
                      "지원/문서/교육": 0,
                      "미팅": 0,
                      "이동": 0
                    }
                  }
                }
                
                entry.dailyEntries?.forEach((day) => {
                  day.activities?.forEach((activity) => {
                    if (acc[key].activities[activity.activityCode] !== undefined) {
                      acc[key].activities[activity.activityCode] += activity.hours || 0
                    }
                  })
                })
                
                return acc
              }, {} as Record<string, { userName: string; teamName: string; userRole: string; activities: Record<string, number> }>)

              const userList = Object.entries(userActivityStats).map(([userId, data]) => ({
                userId,
                userName: data.userName,
                teamName: data.teamName,
                userRole: data.userRole,
                ...data.activities,
                totalHours: Object.values(data.activities).reduce((sum, h) => sum + h, 0)
              })).sort((a, b) => b.totalHours - a.totalHours)

              // 초기 로드 시 MANAGER 권한 사용자만 선택
              useEffect(() => {
                if (userList.length > 0 && selectedUsers.size === 0) {
                  const managerUsers = new Set(
                    userList
                      .filter(u => u.userRole === 'MANAGER')
                      .map(u => u.userId)
                  )
                  setSelectedUsers(managerUsers)
                }
              }, [userList.length])

              return userList.length === 0 ? (
                <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                  <p className="text-gray-500">데이터가 없습니다</p>
                </div>
              ) : (
                <>
                  {/* 직원 선택 체크박스 */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">표시할 직원 선택:</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            // 전체선택
                            const allUsers = new Set(userList.map(u => u.userId))
                            setSelectedUsers(allUsers)
                          }}
                          className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                        >
                          전체선택
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => {
                            // 전체해제
                            setSelectedUsers(new Set())
                          }}
                          className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                        >
                          전체해제
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          onClick={() => {
                            // MANAGER만 선택
                            const managerUsers = new Set(
                              userList
                                .filter(u => u.userRole === 'MANAGER')
                                .map(u => u.userId)
                            )
                            setSelectedUsers(managerUsers)
                          }}
                          className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                        >
                          MANAGER만 선택
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                      {userList.map((user) => (
                        <label key={user.userId} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-100 p-2 rounded transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.userId)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedUsers)
                              if (e.target.checked) {
                                newSelected.add(user.userId)
                              } else {
                                newSelected.delete(user.userId)
                              }
                              setSelectedUsers(newSelected)
                            }}
                            className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <span className="text-gray-700 truncate" title={`${user.userName} (${user.teamName})`}>
                            {user.userName}
                            {user.userRole === 'MANAGER' && <span className="ml-1 text-xs text-orange-600">(팀장)</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 그래프 */}
                  <div className="h-[600px] sm:h-[800px] md:h-[600px]">
                    <ResponsiveContainer width="100%" height="100%">
                      {/* 모바일: 가로 막대 그래프 (horizontal) */}
                      <div className="block md:hidden h-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={userList
                              .filter(u => selectedUsers.has(u.userId))
                              .map(u => ({
                                name: u.userName,
                                fullName: u.userName,
                                teamName: u.teamName,
                                totalHours: u.totalHours,
                                "개발/설계": u["개발/설계"],
                                "영업/견적/PM/기획": u["영업/견적/PM/기획"],
                                "제작/조립/구매": u["제작/조립/구매"],
                                "시험/검증/테스트": u["시험/검증/테스트"],
                                "설치/납품/AS": u["설치/납품/AS"],
                                "지원/문서/교육": u["지원/문서/교육"],
                                "미팅": u["미팅"],
                                "이동": u["이동"]
                              }))}
                            layout="vertical"
                            margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" label={{ value: '시간 (h)', position: 'insideBottom', offset: -5, style: { fontSize: '10px' } }} style={{ fontSize: '10px' }} />
                            <YAxis 
                              type="category" 
                              dataKey="name" 
                              width={0}
                              tick={false}
                            />
                            <Tooltip 
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload
                                  return (
                                    <div className="bg-white p-2 border border-gray-200 rounded-lg shadow-lg">
                                      <p className="font-semibold text-gray-900 mb-1 text-xs">{data.fullName}</p>
                                      <p className="text-xs text-gray-600 mb-2">{data.teamName}</p>
                                      <div className="space-y-1">
                                        {payload.map((entry: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between gap-2">
                                            <span className="text-xs" style={{ color: entry.color }}>
                                              {entry.name}:
                                            </span>
                                            <span className="text-xs font-semibold">{entry.value}h</span>
                                          </div>
                                        ))}
                                        <div className="pt-1 border-t border-gray-200 mt-1">
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="text-xs font-semibold">합계:</span>
                                            <span className="text-xs font-bold text-orange-600">{data.totalHours}h</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                }
                                return null
                              }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '10px' }} />
                            <Bar dataKey="개발/설계" fill="#3B82F6" name="개발/설계" />
                            <Bar dataKey="영업/견적/PM/기획" fill="#10B981" name="영업/견적/PM/기획" />
                            <Bar dataKey="제작/조립/구매" fill="#EAB308" name="제작/조립/구매" />
                            <Bar dataKey="시험/검증/테스트" fill="#EF4444" name="시험/검증/테스트" />
                            <Bar dataKey="설치/납품/AS" fill="#A855F7" name="설치/납품/AS" />
                            <Bar dataKey="지원/문서/교육" fill="#EC4899" name="지원/문서/교육" />
                            <Bar dataKey="미팅" fill="#6366F1" name="미팅" />
                            <Bar dataKey="이동" fill="#14B8A6" name="이동" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* 데스크톱: 세로 막대 그래프 (vertical) */}
                      <div className="hidden md:block h-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={userList.filter(u => selectedUsers.has(u.userId))}
                            margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="userName" 
                              angle={-45} 
                              textAnchor="end" 
                              height={120}
                              style={{ fontSize: '12px' }}
                            />
                            <YAxis label={{ value: '시간 (h)', angle: -90, position: 'insideLeft' }} />
                            <Tooltip 
                              content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload
                                  return (
                                    <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                                      <p className="font-semibold text-gray-900 mb-1">{data.userName}</p>
                                      <p className="text-xs text-gray-600 mb-2">{data.teamName}</p>
                                      <div className="space-y-1">
                                        {payload.map((entry: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between gap-3">
                                            <span className="text-xs" style={{ color: entry.color }}>
                                              {entry.name}:
                                            </span>
                                            <span className="text-xs font-semibold">{entry.value}h</span>
                                          </div>
                                        ))}
                                        <div className="pt-1 border-t border-gray-200 mt-1">
                                          <div className="flex items-center justify-between gap-3">
                                            <span className="text-xs font-semibold">합계:</span>
                                            <span className="text-xs font-bold text-orange-600">{data.totalHours}h</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                }
                                return null
                              }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                            <Bar dataKey="개발/설계" fill="#3B82F6" name="개발/설계" />
                            <Bar dataKey="영업/견적/PM/기획" fill="#10B981" name="영업/견적/PM/기획" />
                            <Bar dataKey="제작/조립/구매" fill="#EAB308" name="제작/조립/구매" />
                            <Bar dataKey="시험/검증/테스트" fill="#EF4444" name="시험/검증/테스트" />
                            <Bar dataKey="설치/납품/AS" fill="#A855F7" name="설치/납품/AS" />
                            <Bar dataKey="지원/문서/교육" fill="#EC4899" name="지원/문서/교육" />
                            <Bar dataKey="미팅" fill="#6366F1" name="미팅" />
                            <Bar dataKey="이동" fill="#14B8A6" name="이동" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </ResponsiveContainer>
                  </div>
                </>
              )
            })()}
          </div>

          {/* 활동 코드별 투입시간 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">활동 코드별 투입시간</h3>
            {activityList.length === 0 ? (
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <p className="text-gray-500">데이터가 없습니다</p>
              </div>
            ) : (
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={activityList} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="activityCode" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis label={{ value: '시간 (h)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Bar dataKey="hours" name="투입시간">
                      {activityList.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getActivityColor(entry.activityCode)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>


        </>
      )}


    </div>
  )
}
